/**
 * SHARED ROOM CONTRACT — the single declaration of the Yjs room shape used by
 * BOTH the browser (roomBridge/useCollab) and the MCP RoomManager. A room is
 * one Y.Doc with these named Y.Maps; values are PLAIN DiagramData objects.
 *
 * The MCP server lives in a separate git repo with an incompatible tsconfig
 * (Node16 rootDir vs. the lib's bundler resolution) and no shared workspace, so
 * it cannot import this file directly. Instead it MIRRORS these constants and a
 * parity test asserts the two declarations stay in lock-step. This file is the
 * canonical source of truth; the MCP mirror must equal it.
 */
export const ROOM_MAPS = {
  nodes: 'nodes',
  edges: 'edges',
  meta: 'meta',
  containers: 'containers',
  layers: 'layers',
  /**
   * Multi-page rooms (RT-8). A Y.Map keyed by pageId → a PageEntry
   * `{ name, order, data }` where `data` is a whole DiagramData (LWW per page).
   * Additive: rooms that only use the top-level single-canvas maps above leave
   * this empty. A multi-canvas editor mirrors its tabs here; the MCP writes a
   * page-targeted diagram here (see create_diagram `page`).
   */
  pages: 'pages',
} as const

/** One page within a multi-page room (value type of the `pages` Y.Map). */
export interface RoomPageEntry {
  name: string
  order: number
  data: unknown // a plain DiagramData object
}

/**
 * Meta Y.Map keys (set inside the `meta` map). `layers` is the draw.io-style
 * layer array (stored under meta, not as a top-level map — it is small and
 * rarely concurrently edited, so whole-array LWW is acceptable).
 */
export const ROOM_META_KEYS = {
  diagramType: 'diagramType',
  title: 'title',
  layers: 'layers',
} as const

/** Stable room key for an edge: its id, or `source->target` when absent. */
export function edgeRoomId(edge: { id?: string; source: string; target: string }): string {
  return edge.id ?? `${edge.source}->${edge.target}`
}
